/**
 * Level 1 feedback: sanity-check the reported program budget against the
 * organization's most recent public IRS Form 990 total functional expenses
 * (`npo_financial_summary.totfuncexpns`, surfaced as `orgAnnualExpenses`).
 *
 * A program budget that eats the whole organization's annual expenses — or
 * that is a rounding error next to them — is usually a data-entry mistake or a
 * multi-year/full-project figure, so we ask for an explanation before moving on.
 */

export type BudgetFlagSeverity = 'critical' | 'warning';

export type BudgetFlagKey = 'atOrOverOrgBudget' | 'veryHigh' | 'verySmall';

export interface BudgetFlag {
  key: BudgetFlagKey;
  severity: BudgetFlagSeverity;
  title: string;
  message: string;
  notePlaceholder: string;
}

// Program budget as a share of the org's annual expenses.
export const AT_OR_OVER_ORG_BUDGET_RATIO = 1;
export const VERY_HIGH_RATIO = 0.8;
export const VERY_SMALL_RATIO = 0.02;

const BUDGET_FLAGS: { [key in BudgetFlagKey]: BudgetFlag } = {
  atOrOverOrgBudget: {
    key: 'atOrOverOrgBudget',
    severity: 'critical',
    title: 'Are you sure this budget is accurate?',
    message:
      'This budget matches or exceeds your organization\'s total annual expenses, which is unusual for a single program. Please ' +
        'check whether it covers multiple years or your full organization.',
    notePlaceholder:
      'Please explain why this program\'s budget is at or above your organization\'s annual expenses.',
  },
  veryHigh: {
    key: 'veryHigh',
    severity: 'warning',
    title: 'Are you sure this budget is accurate?',
    message:
      'This budget accounts for nearly all of your organization\'s annual expenses. That\'s plausible for a single-program ' +
        'organization, but worth a quick check.',
    notePlaceholder:
      'Please explain why this program accounts for nearly all of your organization\'s annual expenses.',
  },
  verySmall: {
    key: 'verySmall',
    severity: 'warning',
    title: 'Are you sure this budget is accurate?',
    message:
      'This looks a little low for a full program. Your budget drives your cost per impact, so it\'s worth a quick check.',
    notePlaceholder:
      'Please explain why this program budget is such a small share of your annual expenses.',
  },
};

export const IRS_SOURCE_TEXT =
  'Based on your organization\'s most recent public IRS Form 990.';

/**
 * Returns the flag to surface, or `undefined` when the budget is plausible or
 * when there is no IRS filing to compare against (no data, no flag, no block).
 */
export const getBudgetFlag = (
  totalBudget: number | undefined,
  orgAnnualExpenses: number | null | undefined,
): BudgetFlag | undefined => {
  if (!totalBudget || totalBudget <= 0) return undefined;
  if (!orgAnnualExpenses || orgAnnualExpenses <= 0) return undefined;

  const ratio = totalBudget / orgAnnualExpenses;

  if (ratio >= AT_OR_OVER_ORG_BUDGET_RATIO) {
    return BUDGET_FLAGS.atOrOverOrgBudget;
  }
  if (ratio >= VERY_HIGH_RATIO) {
    return BUDGET_FLAGS.veryHigh;
  }
  if (ratio < VERY_SMALL_RATIO) {
    return BUDGET_FLAGS.verySmall;
  }
  return undefined;
};
